import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { parkingApi, usePkList, usePkMutation, type InvoiceStatus, type PkInvoice } from "../api/parkingApi";
import { ConsolePage, DetailRow, Drawer, FilterSelect, KpiRow, KpiTile, LoadState, SearchBox, StatusChip, ToolButton, formatDateTime, relativeTime, toneOf, useTicker } from "../components/console/Console";
import FormModal, { type FormField, type FormValues } from "../components/console/FormModal";
import { INVOICE_TONE, errorText, money } from "./parkingUi";
import { SELF_INVOICE_PATH, buildPrefacturaPdf, openPdf } from "./parkingPdf";
import { CFDI_USES, TAX_REGIMES } from "./fiscalCatalogs";

// ===========================================================================
// Control de estacionamientos → Facturación: solicitudes de factura de tickets
// (consola o portal de autofactura). Flujo: solicitada → lista para timbrar
// (con su prefactura en PDF) → emitida, o rechazada. Sin PAC contratado el
// timbrado queda desactivado: se puede registrar el UUID de un CFDI timbrado
// fuera de la plataforma.
// ===========================================================================

const STATES: InvoiceStatus[] = ["requested", "ready_to_stamp", "issued", "rejected"];

export default function InvoicingScreen() {
  const { t, i18n } = useTranslation("parking");
  const [params] = useSearchParams();
  const now = useTicker(60_000);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PkInvoice | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [uuidOpen, setUuidOpen] = useState(false);
  const q = usePkList("invoices", { status: status || undefined, search: search || undefined });
  const all = useMemo(() => q.data ?? [], [q.data]);
  const pacQ = useQuery({ queryKey: ["parking", "pac"], queryFn: parkingApi.pacStatus, staleTime: 300_000 });
  const pacEnabled = Boolean(pacQ.data?.enabled);
  const portalUrl = `${window.location.origin}${SELF_INVOICE_PATH}`;

  useEffect(() => {
    if (params.get("folio")) setRequestOpen(true);
  }, [params]);

  const request = usePkMutation((body: Record<string, string>) => parkingApi.requestInvoice(body));
  const approve = usePkMutation((id: string) => parkingApi.approveInvoice(id));
  const reject = usePkMutation(({ id, reason }: { id: string; reason: string }) => parkingApi.rejectInvoice(id, reason));
  const stamp = usePkMutation((id: string) => parkingApi.stampInvoice(id));
  const issued = usePkMutation(({ id, uuid }: { id: string; uuid: string }) => parkingApi.registerIssued(id, uuid));

  const act = async (fn: () => Promise<PkInvoice>, ok: string) => {
    try {
      const inv = await fn();
      toast.success(ok);
      setSelected(inv);
      return inv;
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
      return null;
    }
  };

  const fiscalFields: FormField[] = [
    { key: "rfc", label: t("invoices.rfc"), required: true, placeholder: "XAXX010101000" },
    { key: "legal_name", label: t("invoices.legalName"), required: true, hint: t("invoices.legalNameHint") },
    { key: "tax_regime", label: t("invoices.taxRegime"), type: "select", required: true, options: TAX_REGIMES.map((r) => ({ value: r, label: `${r} — ${t(`regime.${r}`)}` })) },
    { key: "cfdi_use", label: t("invoices.cfdiUse"), type: "select", required: true, options: CFDI_USES.map((u) => ({ value: u, label: `${u} — ${t(`cfdiUse.${u}`)}` })) },
    { key: "zip", label: t("invoices.zip"), required: true, placeholder: "06600" },
    { key: "email", label: t("invoices.email"), required: true },
  ];

  const total = (s: InvoiceStatus) => all.filter((i) => i.status === s).length;

  return (
    <ConsolePage
      icon="ri-file-text-line"
      title={t("invoices.title")}
      subtitle={t("invoices.subtitle")}
      actions={
        <>
          <ToolButton icon="ri-external-link-line" onClick={() => window.open(portalUrl, "_blank", "noopener")}>{t("invoices.portal")}</ToolButton>
          <ToolButton icon="ri-add-line" variant="primary" onClick={() => setRequestOpen(true)}>{t("invoices.new")}</ToolButton>
        </>
      }
    >
      <div className={`flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-sm ${pacEnabled ? "border-success/40 bg-success/10" : "border-warning/40 bg-warning/10"}`}>
        <i className={pacEnabled ? "ri-shield-check-line text-success" : "ri-error-warning-line text-warning"} />
        <span className="text-content">{pacEnabled ? t("invoices.pacOn", { interpolation: { escapeValue: false }, provider: pacQ.data?.provider }) : t("invoices.pacOff")}</span>
        <button type="button" onClick={() => navigator.clipboard?.writeText(portalUrl).then(() => toast.success(t("common.copied")))} className="ml-auto text-xs font-medium text-primary-500 hover:underline">{t("invoices.copyPortal")}</button>
      </div>
      <KpiRow>
        {STATES.map((s) => (
          <KpiTile key={s} icon={s === "issued" ? "ri-checkbox-circle-line" : s === "rejected" ? "ri-close-circle-line" : s === "ready_to_stamp" ? "ri-quill-pen-line" : "ri-inbox-line"} tone={toneOf(INVOICE_TONE, s)} label={t(`invoiceStatus.${s}`)} value={total(s)} />
        ))}
        <KpiTile icon="ri-money-dollar-circle-line" tone="primary" label={t("invoices.totalAmount")} value={money(all.filter((i) => i.status !== "rejected").reduce((a, i) => a + i.total, 0), i18n.language)} />
        <KpiTile icon="ri-global-line" tone="neutral" label={t("invoices.fromPortal")} value={all.filter((i) => i.source === "portal").length} />
      </KpiRow>
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={search} onChange={setSearch} placeholder={t("invoices.searchPlaceholder")} />
        <FilterSelect value={status} onChange={setStatus} label={t("common.status")} options={[{ value: "", label: t("common.allStatuses") }, ...STATES.map((s) => ({ value: s, label: t(`invoiceStatus.${s}`) }))]} />
      </div>
      <LoadState loading={q.isLoading} error={q.error} empty={!all.length} emptyText={t("invoices.empty")} onRetry={() => q.refetch()}>
        <div className="overflow-x-auto rounded-xl border border-line bg-card">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">{t("invoices.requested")}</th>
                <th className="px-3 py-2">{t("common.folio")}</th>
                <th className="px-3 py-2">{t("invoices.rfc")}</th>
                <th className="px-3 py-2">{t("invoices.legalName")}</th>
                <th className="px-3 py-2">{t("invoices.cfdiUse")}</th>
                <th className="px-3 py-2 text-right">{t("common.total")}</th>
                <th className="px-3 py-2">{t("invoices.prefactura")}</th>
                <th className="px-3 py-2">{t("common.status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {all.map((inv) => (
                <tr key={inv.id} className="cursor-pointer hover:bg-subtle" onClick={() => setSelected(inv)}>
                  <td className="px-3 py-2 text-content-secondary">{relativeTime(inv.requested_at, i18n.language, now)}{inv.source === "portal" && <i className="ri-global-line ml-1 text-content-muted" title={t("invoices.fromPortal")} />}</td>
                  <td className="px-3 py-2 font-mono text-xs text-content">{inv.folio}</td>
                  <td className="px-3 py-2 font-mono text-xs text-content">{inv.rfc}</td>
                  <td className="max-w-[240px] truncate px-3 py-2 text-content">{inv.legal_name}</td>
                  <td className="px-3 py-2 text-content-secondary">{inv.cfdi_use}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-content">{money(inv.total, i18n.language)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-content-secondary">{inv.prefactura_number ?? "—"}</td>
                  <td className="px-3 py-2"><StatusChip tone={toneOf(INVOICE_TONE, inv.status)}>{t(`invoiceStatus.${inv.status}`)}</StatusChip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadState>

      <Drawer
        open={Boolean(selected)}
        title={selected ? `${t("invoices.request")} · ${selected.folio}` : ""}
        subtitle={selected?.lot_name}
        onClose={() => setSelected(null)}
        footer={
          selected && (
            <>
              {selected.status === "requested" && (
                <>
                  <ToolButton icon="ri-close-line" variant="danger" onClick={() => setRejectOpen(true)}>{t("invoices.reject")}</ToolButton>
                  <ToolButton icon="ri-check-line" variant="primary" onClick={() => act(() => approve.mutateAsync(selected.id), t("invoices.approved"))}>{t("invoices.approve")}</ToolButton>
                </>
              )}
              {["ready_to_stamp", "issued"].includes(selected.status) && (
                <ToolButton icon="ri-file-pdf-2-line" onClick={() => openPdf(buildPrefacturaPdf({ invoice: selected, t, lang: i18n.language }), `${selected.prefactura_number}.pdf`)}>{t("invoices.prefacturaPdf")}</ToolButton>
              )}
              {selected.status === "ready_to_stamp" && (
                <>
                  <ToolButton icon="ri-quill-pen-line" variant="primary" disabled={!pacEnabled} title={pacEnabled ? undefined : t("invoices.pacOff")} onClick={() => act(() => stamp.mutateAsync(selected.id), t("invoices.stamped"))}>{t("invoices.stamp")}</ToolButton>
                  <ToolButton icon="ri-file-check-line" onClick={() => setUuidOpen(true)}>{t("invoices.registerUuid")}</ToolButton>
                </>
              )}
            </>
          )
        }
      >
        {selected && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <StatusChip tone={toneOf(INVOICE_TONE, selected.status)}>{t(`invoiceStatus.${selected.status}`)}</StatusChip>
              <span className="text-2xl font-bold tabular-nums text-content">{money(selected.total, i18n.language)}</span>
            </div>
            <DetailRow label={t("invoices.rfc")}><span className="font-mono">{selected.rfc}</span></DetailRow>
            <DetailRow label={t("invoices.legalName")}>{selected.legal_name}</DetailRow>
            <DetailRow label={t("invoices.taxRegime")}>{selected.tax_regime} — {t(`regime.${selected.tax_regime}`)}</DetailRow>
            <DetailRow label={t("invoices.cfdiUse")}>{selected.cfdi_use} — {t(`cfdiUse.${selected.cfdi_use}`)}</DetailRow>
            <DetailRow label={t("invoices.zip")}>{selected.zip}</DetailRow>
            <DetailRow label={t("invoices.email")}>{selected.email}</DetailRow>
            <DetailRow label={t("invoices.subtotal")}>{money(selected.subtotal, i18n.language)}</DetailRow>
            <DetailRow label={t("invoices.iva")}>{money(selected.iva, i18n.language)}</DetailRow>
            <DetailRow label={t("invoices.requested")}>{formatDateTime(selected.requested_at, i18n.language)} · {t(`invoices.source.${selected.source}`)}</DetailRow>
            {selected.prefactura_number && <DetailRow label={t("invoices.prefactura")}>{selected.prefactura_number}</DetailRow>}
            {selected.cfdi_uuid && <DetailRow label={t("invoices.uuid")}><span className="break-all font-mono text-xs">{selected.cfdi_uuid}</span></DetailRow>}
            {selected.rejection_reason && <p className="mt-3 rounded-lg bg-error/10 p-2 text-xs text-error">{selected.rejection_reason}</p>}
            {selected.status === "ready_to_stamp" && !pacEnabled && <p className="mt-3 rounded-lg bg-warning/10 p-2 text-xs text-content-secondary">{t("invoices.readyHint")}</p>}
          </div>
        )}
      </Drawer>

      {requestOpen && (
        <FormModal
          title={t("invoices.new")}
          submitLabel={t("invoices.send")}
          busy={request.isPending}
          onClose={() => setRequestOpen(false)}
          initial={{ code: params.get("folio") ?? "", tax_regime: "601", cfdi_use: "G03" }}
          onSubmit={async (v: FormValues) => {
            try {
              const inv = await request.mutateAsync(Object.fromEntries(Object.entries(v).map(([k, x]) => [k, String(x)])));
              toast.success(t("invoices.created"));
              setRequestOpen(false);
              setSelected(inv);
            } catch (err) {
              toast.error(errorText(err, t("common.saveError")));
            }
          }}
          fields={[{ key: "code", label: t("invoices.ticketCode"), required: true, hint: t("invoices.ticketCodeHint"), full: true }, ...fiscalFields]}
        />
      )}
      {rejectOpen && selected && (
        <FormModal
          title={t("invoices.reject")}
          submitLabel={t("invoices.reject")}
          busy={reject.isPending}
          onClose={() => setRejectOpen(false)}
          onSubmit={async (v: FormValues) => {
            if (await act(() => reject.mutateAsync({ id: selected.id, reason: String(v.reason || "") }), t("invoices.rejected"))) setRejectOpen(false);
          }}
          fields={[{ key: "reason", label: t("invoices.rejectReason"), type: "textarea", required: true }]}
        />
      )}
      {uuidOpen && selected && (
        <FormModal
          title={t("invoices.registerUuid")}
          submitLabel={t("common.save")}
          busy={issued.isPending}
          onClose={() => setUuidOpen(false)}
          onSubmit={async (v: FormValues) => {
            if (await act(() => issued.mutateAsync({ id: selected.id, uuid: String(v.uuid || "") }), t("invoices.registered"))) setUuidOpen(false);
          }}
          fields={[{ key: "uuid", label: t("invoices.uuid"), required: true, placeholder: "XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX", hint: t("invoices.uuidHint"), full: true }]}
        />
      )}
    </ConsolePage>
  );
}
